import {
  Button,
  ButtonGroup,
  DeleteIcon,
  DropdownMenu,
  EditIcon,
  Table,
  TableColumns,
  useDefaultSelecting,
} from '@bp/ui-components';
import dayjs from 'dayjs';
import { observer } from 'mobx-react-lite';
import { useTranslation } from 'react-i18next';
import colors from '../../../assets/colors/Colors.json';
import { useAuthClaims } from '../../../hooks/useAuthClaims';
import { useColumnsSort } from '../../../hooks/useColumnsSort';
import { useHiddenColumns } from '../../../hooks/useHiddenColumns';
import { useMemorizedCacheTag } from '../../../hooks/useMemorizedCacheTag';
import { usePermissionChecker } from '../../../hooks/usePermissionChecker';
import { useSelectedSchoolYear } from '../../../hooks/useSelectedSchoolYear';
import { ColorType } from '../../../types/global';
import {
  use_UpdateSubjectContainersColorsMutation,
  useSubjectContainersListQuery,
} from '../../../types/planung-graphql-client-defs';
import { hexToColorOption } from '../../../utils/colorUtils';
import { useTeachingBlocks } from '../../Teachingblock/hooks/useTeachingBlocks';

type SubjectContainerTableType = {
  uuid: string;
  name: string;
  shortName: string;
  order: number | null;
  validFromName: string | null;
  validUntilName: string | null;
  color: ColorType;
  active: boolean | (() => boolean);
  grades: number[];
  gradeGroups: string[];
};

type SubjectContainerTableProps = {
  loading: boolean;
  onAdd: () => void;
  onEdit: (uuid: string) => void;
  onDelete: (uuids: string[]) => void;
};

export const SubjectContainersTable = observer(({ loading, onAdd, onEdit, onDelete }: SubjectContainerTableProps) => {
  const { t } = useTranslation();
  const { pimAuthClaims } = useAuthClaims();
  const currentSchoolYear = useSelectedSchoolYear();
  const context = useMemorizedCacheTag('SUBJECT_CONTAINER');

  const permissions = usePermissionChecker();
  const readonly = !permissions?.canWriteMasterData();

  const { columnVisibility, saveColumnVisibility } = useHiddenColumns('subject-container-list', {
    validUntilName: false,
    validFromName: false,
  });

  const { sorting, saveSorting } = useColumnsSort('subject-container-list');
  const { rowSelection, onRowSelectionChange, clearSelection } = useDefaultSelecting();

  const [, updateColor] = use_UpdateSubjectContainersColorsMutation();

  /** Je Schiene eine zufällige Farbe — wie bei Fächern, Personen und Klassen unabhängig gezogen. */
  const setRandomColors = async (uuids: string[]) => {
    await Promise.all(
      uuids.map((uuid) =>
        updateColor({ uuid, color: colors[Math.floor(Math.random() * colors.length)].html }, context),
      ),
    );
  };

  const [{ data: subjectContainersData }] = useSubjectContainersListQuery({
    context,
  });

  const { teachingBlocks } = useTeachingBlocks(currentSchoolYear?.uuid);

  const columns: TableColumns<SubjectContainerTableType>[] = [
    {
      header: t('subjectContainer.order'),
      accessorKey: 'order',
      id: 'order',
      size: 70,
    },
    {
      header: t('common.name'),
      accessorKey: 'name',
      id: 'name',
      canExpand: true,
    },
    {
      header: t('subjectContainer.shortName'),
      accessorKey: 'shortName',
      id: 'shortName',
      size: 150,
    },
    {
      header: t('grades.title', { count: 2 }),
      id: 'grades',
      accessorKey: 'grades',
      size: 150,
      cell: ({ row }) => {
        return row.original.grades.sort().join(', ');
      },
    },
    {
      header: t('gradeGroups.title', { count: 2 }),
      id: 'gradeGroups',
      accessorKey: 'gradeGroups',
      size: 150,
      cell: ({ row }) => {
        return row.original.gradeGroups.sort().join(', ');
      },
    },

    {
      header: t('subjectContainer.validFrom'),
      accessorKey: 'validFromName',
      id: 'validFromName',
    },
    {
      header: t('subjectContainer.validUntil'),
      accessorKey: 'validUntilName',
      id: 'validUntilName',
    },

    {
      header: t('common.color'),
      accessorKey: 'color',
      id: 'color',
      type: 'color',
      size: 100,
    },
    {
      header: t('common.active.full'),
      accessorKey: 'active',
      id: 'active',
      type: 'active',
    },
  ];

  const data: SubjectContainerTableType[] =
    subjectContainersData?.subjectContainers.map((subjectContainer) => {
      const teachinBlock = teachingBlocks?.find((t) => t.subjectContainerUuid === subjectContainer.uuid);
      const { html, label } = hexToColorOption(subjectContainer.timetableConfig?.color ?? '');

      return {
        uuid: subjectContainer.uuid,
        color: {
          color: html,
          colorLabel: label,
        },
        name: subjectContainer.name,
        shortName: subjectContainer.shortName,
        order: subjectContainer.order ?? 0,
        active:
          dayjs(subjectContainer.validFrom?.start) <= dayjs() &&
          dayjs(subjectContainer.validUntil?.end) >= dayjs() &&
          !!teachinBlock,
        validFromName: subjectContainer.validFrom?.name ?? '',
        validUntilName: subjectContainer.validUntil?.name ?? '',
        grades: subjectContainer.grades,
        gradeGroups: subjectContainer.gradeGroups.map((gg) => gg.shortName),
      };
    }) ?? [];

  return (
    <Table<SubjectContainerTableType>
      showBorderRadius
      showShadow
      canScroll
      loading={loading}
      minHeight={600}
      isDesktop={true}
      onSortingChange={saveSorting}
      sorting={sorting}
      columnVisibility={columnVisibility}
      onColumnVisibilityChange={saveColumnVisibility}
      showVisibility
      printerSettings={{
        headline: pimAuthClaims.getProfile()?.organization.name,
        subline: `${t('subjectContainer.title', { count: 2 })} - ${t('common.schoolYear')} ${currentSchoolYear?.shortName}`,
        filename: `${t('subjectContainer.title', { count: 2 })}_${currentSchoolYear?.shortName}`,
      }}
      showActionBar
      actionBarSettings={{
        showExpertFilter: true,
        showAddButton: true,
        disableAddButton: readonly,
        showPrintButton: true,
        showBulkEdit: true,
        disableBulkEdit: readonly,
      }}
      isOnWhite={false}
      data={data}
      columns={columns}
      showSelect
      rowSelection={rowSelection}
      onRowSelectionChange={onRowSelectionChange}
      bulkEditDropdownContent={(rows) => (
        <DropdownMenu
          data={[
            {
              label: t('common.addRandomColor'),
              onClick: async () => {
                await setRandomColors(rows.map(({ original }) => original.uuid));
                clearSelection();
              },
            },
            { type: 'ruler' },
            {
              label: t('common.delete'),
              color: 'error',
              onClick: () => {
                onDelete(rows.map(({ original }) => original.uuid));
                clearSelection();
              },
            },
          ]}
        />
      )}
      onAddClick={onAdd}
      lastColWidth='80px'
      lastCol={(row) => {
        return (
          <ButtonGroup disabled={readonly}>
            <Button
              hierarchy='tertiary'
              type='button'
              onClick={() => onEdit(row.original.uuid)}
              icon={<EditIcon className='small' />}
              disabled={readonly}
            />
            <Button
              hierarchy='tertiary'
              icon={<DeleteIcon className='small' />}
              onClick={() => onDelete([row.original.uuid])}
              disabled={readonly}
            />
          </ButtonGroup>
        );
      }}
    />
  );
});
